import {FC, useCallback, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import styled from "styled-components";
import {isNumber, partial} from "lodash";
import {
	getAvatarImage,
	getAvatars,
	getIsLoadingChangeAvatar,
	getIsOpenChangeAvatarModal,
	getIsShowChangedAvatarText,
} from "modules/selectors";
import {changeAvatar, hideModal} from "modules/actions";
import {IMAGES_URL} from "modules/constants";
import {PrimaryButton, PureButton} from "components";
import {ButtonsBox} from "./common";
import {BaseModal} from "./BaseModal";

const Container = styled.div`
	display: grid;
	grid-template-rows: auto;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	grid-row-gap: 12px;
	grid-column-gap: 12px;
	margin-bottom: 20px;

	@media (min-width: 769px) {
		grid-template-columns: repeat(4, minmax(0, 1fr));
		grid-row-gap: 20px;
		grid-column-gap: 20px;
	}
`;

const AvatarItem = styled(PureButton)<{isSelected: boolean}>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 0;
	padding-top: 100%;
	position: relative;
	border: 3px solid
		${({theme, isSelected}) => (isSelected ? theme.background.secondary : "transparent")};
	border-radius: 1px;
`;

const Avatar = styled.img`
	position: absolute;
	left: 0;
	right: 0;
	top: 0;
	bottom: 0;
	cursor: pointer;
	width: 100%;
	height: 100%;
	object-fit: contain;
	padding: 2px;
`;

const SavedText = styled.div`
	display: flex;
	align-items: center;
	max-width: 220px;
	margin: 10px auto;
	color: #1ac447;
	min-height: 42px;

	@media (min-width: 769px) {
		max-width: 300px;
	}
`;

export const ChangeAvatarModal: FC = () => {
	const dispatch = useDispatch();
	const {t} = useTranslation();
	const modalRef = useRef<HTMLDivElement>(null);
	const isOpen = useSelector(getIsOpenChangeAvatarModal);
	const isLoading = useSelector(getIsLoadingChangeAvatar);
	const isShowChangedAvatarText = useSelector(getIsShowChangedAvatarText);
	const avatars = useSelector(getAvatars);
	const userAvatarImage = useSelector(getAvatarImage);
	const userAvatarId = avatars.find(({avatar}) => avatar === userAvatarImage)?.id;
	const [avatarId, setAvatarId] = useState<number | null>(userAvatarId || null);
	const hasChanges = avatarId !== userAvatarId;
	const isDisabled = !isNumber(avatarId) || isLoading || !hasChanges;

	const handleClose = useCallback(() => {
		dispatch(hideModal("changeAvatar"));
	}, [dispatch]);

	const submitHandler = useCallback(() => {
		if (avatarId) {
			dispatch(changeAvatar({id: avatarId}));
		}
	}, [dispatch, avatarId]);

	const selectHandler = useCallback(
		(id: number) => {
			setAvatarId(id);

			if (modalRef.current) {
				modalRef.current.scrollTo({
					top: modalRef.current.scrollHeight,
					behavior: "smooth",
				});
			}
		},
		[setAvatarId]
	);

	if (!isOpen) {
		return null;
	}

	return (
		<BaseModal handleClose={handleClose} modalRef={modalRef}>
			<Container>
				{avatars.map(({id, avatar}) => (
					<AvatarItem
						key={id}
						onClick={partial(selectHandler, id)}
						isSelected={id === avatarId}>
						<Avatar src={`${IMAGES_URL}image/${avatar}`} alt="" />
					</AvatarItem>
				))}
			</Container>

			<ButtonsBox>
				{isShowChangedAvatarText ? (
					<SavedText>{t("change_avatar.modal.saved")}</SavedText>
				) : (
					<PrimaryButton onClick={submitHandler} disabled={isDisabled}>
						{t("change_avatar.modal.button")}
					</PrimaryButton>
				)}
			</ButtonsBox>
		</BaseModal>
	);
};
